import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import Popover from "../../Popover";
import Icon from "../../Icon";
import Button from "../../Button";
import BaseSection, { AvatarLink, Item } from "../components/BaseSection";

/**
 * PinnedIconWrapper - Positions the pin icon absolutely, vertically centered on the right
 * This ensures consistent alignment with avatars and text across all pinned items
 */
const PinnedIconWrapper = styled.div`
  position: absolute;
  top: 50%;
  right: ${(props) => themeGet("space.6")(props)};
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  z-index: 10;
  flex-shrink: 0;
`;

/**
 * SideNavPinnedSection - Renders the "Pinned" section of the side navigation
 *
 * Handles presentation and layout. Business logic (onUnpin callback) is passed as a prop.
 *
 * @param {Array} items - Array of pinned items with avatar, name, link, shape, onUnpin, gradient
 * @param {boolean} isExpanded - Whether the sidebar is expanded
 */
const SideNavPinnedSection = ({ items, isExpanded }) => {
  const renderPinnedItem = (item, isExpanded) => {
    const { avatar, name, link, shape, onUnpin, gradient } = item;

    // Skip rendering if required props are missing
    if (!name || !link) {
      return null;
    }

    return (
      <Item key={name}>
        {isExpanded ? (
          <>
            <AvatarLink
              avatar={avatar}
              name={name}
              link={link}
              shape={shape}
              showName={true}
              gradient={gradient}
            />
            {onUnpin && (
              <PinnedIconWrapper>
                <Popover
                  text="Unpin item"
                  direction="right"
                  width="fit-content"
                  tabIndex="-1"
                >
                  <Button
                    height="20px"
                    width="20px"
                    borderRadius="20px"
                    variant="borderGrey"
                    borderColor="white"
                    onClick={() => onUnpin?.(item)}
                    small
                    iconOnly
                    px="0"
                    py="0"
                  >
                    <Icon
                      icon={["fas", "thumbtack"]}
                      color="greyDarkest"
                      size="sm"
                    />
                  </Button>
                </Popover>
              </PinnedIconWrapper>
            )}
          </>
        ) : (
          <Popover text={name} direction="right" width="fit-content">
            <AvatarLink
              avatar={avatar}
              name={name}
              link={link}
              shape={shape}
              showName={false}
              gradient={gradient}
            />
          </Popover>
        )}
      </Item>
    );
  };

  return (
    <BaseSection
      title="Pinned"
      items={items || []}
      isExpanded={isExpanded}
      renderItem={renderPinnedItem}
    />
  );
};

SideNavPinnedSection.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string,
      name: PropTypes.string.isRequired,
      link: PropTypes.string.isRequired,
      shape: PropTypes.string,
      onUnpin: PropTypes.func,
    })
  ),
  isExpanded: PropTypes.bool,
};

export default SideNavPinnedSection;
